import {ReccoTopicDTO} from '@/ts/types/dto/recco/topic.dto';
import {LibraryContentTypeDTO, LibraryMediaTypeDTO} from '@/ts/types/dto/recco/library.dto';
import {ReccoRecommendationTypeDTO} from '@/ts/types/dto/recco/recommendation.dto';
import type {Content} from '@/ts/types/dto/interventions-dto';
import {mediaDurationMinutes} from '@/ts/utils/pure-functions';

// i18n key maps shared by the Explore filter bar, library cards and page grouping.
// Keep them in one place so the dropdown label and the card label can never drift apart.

/** Media-type verb labels (educational content): Watch/Read/Listen. */
export const mediaTypeLabelKey: Record<LibraryMediaTypeDTO, string> = {
  [LibraryMediaTypeDTO.Watch]: 'recco_media_type_videos',
  [LibraryMediaTypeDTO.Read]: 'recco_media_type_articles',
  [LibraryMediaTypeDTO.Listen]: 'recco_media_type_audios',
};

/** Consumed status markers (past tense) shown once the item is opened: Watched/Read/Listened (IPD-2943). */
export const statusMarkerLabelKey: Record<LibraryMediaTypeDTO, string> = {
  [LibraryMediaTypeDTO.Watch]: 'recco_status_watched',
  [LibraryMediaTypeDTO.Read]: 'recco_status_read',
  [LibraryMediaTypeDTO.Listen]: 'recco_status_listened',
};

/** Derive a content block's media type from its media fields: video → Watch, audio → Listen, else Read. */
export function contentMediaType(content: Content): LibraryMediaTypeDTO {
  if (content.video) {
    return LibraryMediaTypeDTO.Watch;
  }
  if (content.audio) {
    return LibraryMediaTypeDTO.Listen;
  }
  return LibraryMediaTypeDTO.Read;
}

/**
 * Whether the item has something to play — drives the play badge on cards. Both card families
 * reduce to this enum so they can't drift, each feeding it the field it already labels from:
 * the recommendation band derives it from the content's media fields, library cards pass
 * `mediaType`. Keying a card's badge off a different field than its visible media verb is what
 * would let it print "Audios" with no badge.
 */
export function isPlayableMediaType(mediaType: LibraryMediaTypeDTO | null | undefined): boolean {
  return mediaType === LibraryMediaTypeDTO.Watch || mediaType === LibraryMediaTypeDTO.Listen;
}

/**
 * Media type → the recommendation type the cards label it with. Lets a card derive its label
 * through `contentMediaType` instead of re-deriving the video/audio/text precedence, so the
 * label, the media verb and the play badge provably come from one function.
 */
export const recommendationTypeByMediaType: Record<LibraryMediaTypeDTO, ReccoRecommendationTypeDTO> = {
  [LibraryMediaTypeDTO.Watch]: ReccoRecommendationTypeDTO.Video,
  [LibraryMediaTypeDTO.Listen]: ReccoRecommendationTypeDTO.Audio,
  [LibraryMediaTypeDTO.Read]: ReccoRecommendationTypeDTO.Article,
};

/**
 * The label a card prints for a piece of media with its length behind it: "Listen · 12 min"
 * (IPD-3375). One place rather than a join per card, so the separator and the rounding cannot
 * differ between a Today tile, an Explore card and the content page, and so the label reads the
 * same as the mobile apps' "Listen · 12 min". The length is left off when the CMS holds none for
 * the file, rather than printed as zero: a piece with an unknown length is not a short one.
 */
export function mediaLabelWithDuration(
  label: string,
  durationSeconds: number | null | undefined,
  t: (key: string, named: Record<string, unknown>) => string,
): string {
  const minutes = mediaDurationMinutes(durationSeconds);
  return minutes === null ? label : `${label} · ${t('media_duration_minutes', {n: minutes})}`;
}

/** Content-type labels: Challenge/Program/Educational. */
export const contentTypeLabelKey: Record<LibraryContentTypeDTO, string> = {
  [LibraryContentTypeDTO.Challenge]: 'explore_filter_content_type_challenge',
  [LibraryContentTypeDTO.Program]: 'explore_filter_content_type_program',
  [LibraryContentTypeDTO.Educational]: 'explore_filter_content_type_educational',
};

/** Topic labels: Movement/Nutrition/Mental Health/Relaxation/Sleep. */
export const topicLabelKey: Record<ReccoTopicDTO, string> = {
  [ReccoTopicDTO.PHYSICAL_ACTIVITY]: 'recco_physical_activity',
  [ReccoTopicDTO.NUTRITION]: 'recco_nutrition',
  [ReccoTopicDTO.MENTAL_WELLBEING]: 'recco_mental_wellbeing',
  [ReccoTopicDTO.RELAXATION]: 'recco_relaxation',
  [ReccoTopicDTO.SLEEP]: 'recco_sleep',
};
